/**
 * Style Personality Quiz
 * Determines user's stationery aesthetic and provides personalized recommendations
 * with match scores and explanations
 */

const _isDev =
  window.location.hostname.includes('myshopify.com') || window.location.hostname === 'localhost';
const _err = _isDev ? console.error.bind(console) : () => {};

window.StyleQuiz = {
  answers: {},
  personality: null,
  recommendedProducts: [],
  productCache: null, // Cache fetched products to avoid repeated API calls
  productCacheTime: null,

  // Personality definitions with associated tags and traits
  // Tags include expanded synonyms for better matching against real product data
  personalities: {
    'cottage-curator': {
      title: 'Cottage Core Curator',
      emoji: '🌿',
      description:
        'You find beauty in the simple things. Pressed flowers, handwritten letters, and sun-dappled afternoons with a good book speak to your soul. Your stationery collection tells stories of nature and nostalgia.',
      traits: ['Nature lover', 'Romantic at heart', 'Detail-oriented'],
      // Expanded tags with synonyms and variations
      tags: [
        'flora',
        'nature',
        'floral',
        'botanical',
        'flower',
        'plant',
        'garden',
        'leaf',
        'leaves',
        'mushroom',
        'cottage',
        'countryside',
        'rustic',
        'cozy',
        'vintage',
        'romantic',
        'pressed',
        'wildflower',
        'meadow',
        'herbs',
        'greenery',
        'forest',
      ],
      // Title keywords for semantic matching
      titleKeywords: [
        'flower',
        'floral',
        'botanical',
        'nature',
        'garden',
        'plant',
        'leaf',
        'mushroom',
        'cottage',
        'meadow',
        'forest',
        'herb',
        'rose',
        'daisy',
        'sunflower',
        'lavender',
        'wildflower',
        'bloom',
        'blossom',
      ],
      colors: ['earth-tones', 'pastels', 'warm-neutrals'],
      productTypes: ['sticker', 'washi', 'paper', 'journal'],
    },
    'dark-scholar': {
      title: 'Dark Academia Scholar',
      emoji: '📚',
      description:
        'Old libraries, candlelit study sessions, and the smell of aged paper call to you. You appreciate the weight of history and the elegance of classic aesthetics. Your desk is a sanctuary of scholarly pursuits.',
      traits: ['Intellectual', 'Classic taste', 'Thoughtful'],
      tags: [
        'vintage',
        'stamp',
        'stamps',
        'book',
        'books',
        'letter',
        'letters',
        'journal',
        'notebook',
        'classic',
        'elegant',
        'sticker',
        'washi',
        'library',
        'literary',
        'writing',
        'script',
        'calligraphy',
        'antique',
        'academic',
        'scholarly',
        'poetry',
        'manuscript',
        'paper',
        'old',
        'ephemera',
        'postcard',
        'postal',
        'mail',
      ],
      titleKeywords: [
        'vintage',
        'stamp',
        'book',
        'letter',
        'library',
        'literary',
        'classic',
        'elegant',
        'antique',
        'manuscript',
        'poetry',
        'postcard',
        'postal',
        'ephemera',
        'journal',
        'notebook',
        'writing',
        'script',
        'old',
        'aged',
        'scholar',
        'academic',
      ],
      colors: ['jewel-tones', 'monochrome', 'earth-tones'],
      productTypes: ['stamp', 'journal', 'notebook', 'paper', 'sticker'],
    },
    'minimalist-maker': {
      title: 'Minimalist Maker',
      emoji: '✨',
      description:
        'Less is more for you. Clean lines, purposeful design, and quality over quantity guide your choices. Every piece in your collection earns its place through beauty and function.',
      traits: ['Intentional', 'Quality-focused', 'Calm'],
      tags: [
        'new arrivals',
        'modern',
        'clean',
        'simple',
        'functional',
        'minimal',
        'basic',
        'neutral',
        'plain',
        'solid',
        'geometric',
        'line',
        'mono',
        'black',
        'white',
        'elegant',
        'sleek',
        'contemporary',
        'planner',
        'organize',
        'productivity',
      ],
      titleKeywords: [
        'minimal',
        'simple',
        'clean',
        'basic',
        'neutral',
        'modern',
        'plain',
        'solid',
        'geometric',
        'line',
        'black',
        'white',
        'elegant',
        'sleek',
        'planner',
        'organizer',
        'productivity',
        'classic',
      ],
      colors: ['monochrome', 'warm-neutrals', 'pastels'],
      productTypes: ['journal', 'planner', 'notebook', 'tape'],
    },
    'whimsical-dreamer': {
      title: 'Whimsical Dreamer',
      emoji: '🦋',
      description:
        'Magic lives in the everyday for you. Fairytales, fantasy worlds, and enchanted forests spark your imagination. Your stationery is a portal to wonder and possibility.',
      traits: ['Imaginative', 'Playful', 'Creative'],
      tags: [
        'fantasy',
        'magic',
        'magical',
        'fairy',
        'fairytale',
        'folktale',
        'enchanted',
        'sticker',
        'stickers',
        'whimsical',
        'dream',
        'dreamy',
        'unicorn',
        'mermaid',
        'butterfly',
        'star',
        'stars',
        'moon',
        'celestial',
        'sky',
        'cloud',
        'rainbow',
        'sparkle',
        'glitter',
        'cute',
        'kawaii',
        'sweet',
        'pastel',
        'animal',
        'cat',
        'bunny',
        'bear',
      ],
      titleKeywords: [
        'magic',
        'fairy',
        'dream',
        'fantasy',
        'enchanted',
        'whimsical',
        'unicorn',
        'mermaid',
        'butterfly',
        'star',
        'moon',
        'celestial',
        'cloud',
        'rainbow',
        'sparkle',
        'cute',
        'kawaii',
        'sweet',
        'animal',
        'cat',
        'bunny',
        'bear',
        'mushroom',
        'forest',
      ],
      colors: ['pastels', 'jewel-tones'],
      productTypes: ['sticker', 'washi', 'tape', 'paper'],
    },
    'vintage-collector': {
      title: 'Vintage Collector',
      emoji: '🕰️',
      description:
        'You believe the past holds treasures worth preserving. Ephemera, antique postcards, and time-worn textures make your heart sing. Your collection is a love letter to eras gone by.',
      traits: ['Nostalgic', 'Appreciative', 'Curator'],
      tags: [
        'vintage',
        'antique',
        'retro',
        'stamp',
        'stamps',
        'historical',
        'ephemera',
        'old',
        'classic',
        'nostalgic',
        'postcard',
        'postal',
        'mail',
        'letter',
        'aged',
        'distressed',
        'sepia',
        'brown',
        'paper',
        'ticket',
        'label',
        'victorian',
        'european',
        'french',
        'english',
        'botanical',
        'map',
        'travel',
      ],
      titleKeywords: [
        'vintage',
        'antique',
        'retro',
        'stamp',
        'ephemera',
        'old',
        'classic',
        'postcard',
        'postal',
        'letter',
        'aged',
        'sepia',
        'ticket',
        'label',
        'victorian',
        'french',
        'map',
        'travel',
        'nostalgic',
      ],
      colors: ['earth-tones', 'warm-neutrals', 'jewel-tones'],
      productTypes: ['stamp', 'paper', 'sticker', 'ephemera'],
    },
    'bold-expressionist': {
      title: 'Bold Expressionist',
      emoji: '🎨',
      description:
        'Color is your language and creativity is your superpower. You are not afraid to mix patterns, layer textures, and make a statement. Your stationery reflects your vibrant inner world.',
      traits: ['Expressive', 'Fearless', 'Artistic'],
      tags: [
        'sticker',
        'stickers',
        'washi',
        'colorful',
        'fun',
        'creative',
        'new arrivals',
        'bright',
        'bold',
        'vibrant',
        'art',
        'artistic',
        'pattern',
        'abstract',
        'pop',
        'neon',
        'color',
        'multicolor',
        'graphic',
        'design',
        'mixed',
        'collage',
        'craft',
        'diy',
        'decorative',
        'aesthetic',
      ],
      titleKeywords: [
        'colorful',
        'bright',
        'bold',
        'vibrant',
        'art',
        'artistic',
        'pattern',
        'abstract',
        'pop',
        'neon',
        'color',
        'graphic',
        'design',
        'collage',
        'craft',
        'decorative',
        'creative',
        'fun',
      ],
      colors: ['pastels', 'jewel-tones'],
      productTypes: ['sticker', 'washi', 'tape', 'paper'],
    },
  },

  // Mapping from quiz answers to personality weights
  answerWeights: {
    aesthetic: {
      'cottage-core': { 'cottage-curator': 10, 'vintage-collector': 3 },
      'dark-academia': { 'dark-scholar': 10, 'vintage-collector': 3 },
      minimalist: { 'minimalist-maker': 10 },
      whimsical: { 'whimsical-dreamer': 10, 'bold-expressionist': 2 },
      vintage: { 'vintage-collector': 10, 'dark-scholar': 3, 'cottage-curator': 2 },
      artsy: { 'bold-expressionist': 10, 'whimsical-dreamer': 2 },
    },
    activity: {
      'reading-cafe': { 'dark-scholar': 5, 'cottage-curator': 3, 'minimalist-maker': 2 },
      'nature-hike': { 'cottage-curator': 5, 'whimsical-dreamer': 2 },
      crafting: { 'bold-expressionist': 5, 'whimsical-dreamer': 3, 'vintage-collector': 2 },
      'antique-shopping': { 'vintage-collector': 5, 'dark-scholar': 3 },
      museum: { 'dark-scholar': 4, 'vintage-collector': 3, 'minimalist-maker': 2 },
      journaling: { 'minimalist-maker': 3, 'cottage-curator': 3, 'dark-scholar': 2 },
    },
    palette: {
      'earth-tones': { 'cottage-curator': 4, 'vintage-collector': 3, 'dark-scholar': 2 },
      'jewel-tones': { 'dark-scholar': 4, 'vintage-collector': 2, 'bold-expressionist': 2 },
      pastels: { 'whimsical-dreamer': 4, 'cottage-curator': 2, 'minimalist-maker': 2 },
      monochrome: { 'minimalist-maker': 5, 'dark-scholar': 3 },
      'warm-neutrals': { 'minimalist-maker': 3, 'cottage-curator': 3, 'vintage-collector': 2 },
    },
    organization: {
      'bullet-journal': { 'minimalist-maker': 3, 'dark-scholar': 2 },
      planner: { 'minimalist-maker': 4, 'dark-scholar': 2 },
      'sticky-notes': { 'bold-expressionist': 3, 'whimsical-dreamer': 2 },
      'aesthetic-spread': { 'cottage-curator': 3, 'whimsical-dreamer': 3, 'bold-expressionist': 2 },
      freeform: { 'whimsical-dreamer': 3, 'bold-expressionist': 2, 'cottage-curator': 2 },
    },
  },

  init: function () {
    const startBtn = document.getElementById('startStyleQuizBtn');
    if (startBtn) {
      startBtn.addEventListener('click', () => this.startQuiz());
    }

    // Also listen for any quiz option clicks via event delegation
    const quiz = document.getElementById('stylePersonalityQuiz');
    if (quiz) {
      quiz.addEventListener('click', (e) => {
        // Handle clicks on quiz option buttons that may not have inline handlers
        const btn = e.target.closest('.style-quiz__option');
        if (btn && btn.dataset.question && btn.dataset.value && btn.dataset.next) {
          this.answer(btn.dataset.question, btn.dataset.value, btn.dataset.next);
        }
      });
    }
  },

  startQuiz: function () {
    this.answers = {};
    this.personality = null;
    this.showScreen('q1');
  },

  answer: function (question, value, nextScreen) {
    this.answers[question] = value;

    if (nextScreen === 'personality') {
      this.calculatePersonality();
      this.showPersonalityReveal();
    } else {
      this.showScreen(nextScreen);
    }
  },

  showScreen: function (screenId) {
    const quiz = document.getElementById('stylePersonalityQuiz');
    if (!quiz) {
      _err('Quiz container not found');
      return;
    }

    quiz.querySelectorAll('.style-quiz__screen').forEach((screen) => {
      screen.classList.remove('style-quiz__screen--active');
    });

    const targetScreen = quiz.querySelector(`[data-screen="${screenId}"]`);
    if (targetScreen) {
      targetScreen.classList.add('style-quiz__screen--active');
    }

    // Scroll to top of quiz with Safari fallback
    try {
      quiz.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (_e) {
      // Fallback for older browsers
      quiz.scrollIntoView(true);
    }
  },

  goBack: function (screenId) {
    this.showScreen(screenId);
  },

  calculatePersonality: function () {
    const scores = {};

    // Initialize scores
    Object.keys(this.personalities).forEach((key) => {
      scores[key] = 0;
    });

    // Calculate weighted scores based on answers
    Object.keys(this.answers).forEach((question) => {
      const answer = this.answers[question];
      const weights = this.answerWeights[question];

      if (weights && weights[answer]) {
        Object.keys(weights[answer]).forEach((personality) => {
          scores[personality] += weights[answer][personality];
        });
      }
    });

    // Find highest scoring personality
    let maxScore = 0;
    let topPersonality = 'cottage-curator';

    Object.keys(scores).forEach((key) => {
      if (scores[key] > maxScore) {
        maxScore = scores[key];
        topPersonality = key;
      }
    });

    this.personality = topPersonality;
    this.personalityScores = scores;

    // Save to localStorage for attribution
    this.saveQuizAttribution();

    return topPersonality;
  },

  saveQuizAttribution: function () {
    try {
      const attribution = {
        _quiz_source: 'Style-quiz',
        _quiz_personality: this.personality,
        _quiz_answers: JSON.stringify(this.answers),
        _timestamp: new Date().toISOString(),
      };
      localStorage.setItem('coracrea_quiz_attribution', JSON.stringify(attribution));
    } catch (e) {
      _err('Error saving quiz attribution:', e);
    }
  },

  showPersonalityReveal: function () {
    this.showScreen('personality');

    // Ensure personality is set
    if (!this.personality || !this.personalities[this.personality]) {
      _err('No personality calculated');
      this.personality = 'cottage-curator'; // Fallback to default
    }

    const p = this.personalities[this.personality];

    // Update personality content with null checks
    const titleEl = document.getElementById('personalityTitle');
    const descEl = document.getElementById('personalityDescription');
    const traitsContainer = document.getElementById('personalityTraits');

    if (titleEl) titleEl.textContent = p.title;
    if (descEl) descEl.textContent = p.description;

    // Build traits
    if (traitsContainer) {
      traitsContainer.innerHTML = '';
      p.traits.forEach((trait) => {
        const span = document.createElement('span');
        span.className = 'style-quiz__trait';
        span.textContent = trait;
        traitsContainer.appendChild(span);
      });
    }

    // Trigger confetti animation
    this.triggerConfetti();

    // Fix share button SVG colors after screen is shown
    setTimeout(fixShareButtonSVGs, 100);
    setTimeout(fixShareButtonSVGs, 500);

    // Track completion
    if (typeof gtag === 'function') {
      gtag('event', 'style_quiz_complete', {
        event_category: 'Style Quiz',
        event_label: this.personality,
      });
    }
  },

  triggerConfetti: function () {
    const container = document.querySelector('.style-quiz__confetti-container');
    if (!container) return;

    container.innerHTML = '';

    const emojis = ['✨', '🌟', '💫', '🎉', '🎊', '⭐'];
    for (let i = 0; i < 30; i++) {
      const confetti = document.createElement('span');
      confetti.className = 'style-quiz__confetti-piece';
      confetti.textContent = emojis[Math.floor(Math.random() * emojis.length)];
      confetti.style.left = Math.random() * 100 + '%';
      confetti.style.animationDelay = Math.random() * 2 + 's';
      confetti.style.animationDuration = 2 + Math.random() * 2 + 's';
      container.appendChild(confetti);
    }
  },

  showProducts: function () {
    this.showScreen('results');

    // Ensure personality is set
    if (!this.personality || !this.personalities[this.personality]) {
      _err('No personality set');
      this.personality = 'cottage-curator'; // Fallback
    }

    // Update results badge with null checks
    const p = this.personalities[this.personality];
    const badgeEmoji = document.getElementById('badgeEmoji');
    const badgeTitle = document.getElementById('badgeTitle');
    if (badgeEmoji) badgeEmoji.textContent = p.emoji;
    if (badgeTitle) badgeTitle.textContent = p.title;

    this.loadRecommendations();
  },

  loadRecommendations: function () {
    const primaryPersonality = this.personalities[this.personality];
    const secondaryPersonalities = this.getSecondaryPersonalities();

    // Use cached products if available and fresh (5 minute cache)
    const cacheValid =
      this.productCache &&
      this.productCacheTime &&
      Date.now() - this.productCacheTime < 5 * 60 * 1000;

    const processProducts = (rawProducts) => {
      // Filter available products
      let products = rawProducts.filter((product) => {
        return product.variants && product.variants[0] && product.variants[0].available;
      });

      // Score each product against primary and secondary personalities
      products = products.map((product) => {
        const scoreData = this.scoreProductSmart(
          product,
          primaryPersonality,
          secondaryPersonalities
        );
        return {
          ...product,
          matchScore: scoreData.score,
          matchReasons: scoreData.reasons,
        };
      });

      // Sort by score
      products.sort((a, b) => b.matchScore - a.matchScore);

      // Take top matches - expanded pool for better diversity selection
      const topProducts = products.slice(0, 24);

      // Ensure diversity across product types - now showing 8 products
      const selectedProducts = this.ensureDiversity(topProducts, 8);

      // If we still have few results, add more from top scores
      if (selectedProducts.length < 6) {
        const remaining = products.filter((p) => !selectedProducts.includes(p));
        while (selectedProducts.length < 8 && remaining.length > 0) {
          selectedProducts.push(remaining.shift());
        }
      }

      this.recommendedProducts = selectedProducts;
      this.displayProducts(selectedProducts);
    };

    if (cacheValid) {
      processProducts(this.productCache);
      return;
    }

    fetch('/collections/all/products.json?limit=250')
      .then((response) => response.json())
      .then((data) => {
        const products = data.products || [];
        // Cache the products
        this.productCache = products;
        this.productCacheTime = Date.now();
        processProducts(products);
      })
      .catch((err) => {
        _err('Error loading recommendations:', err);
        this.displayFallback();
      });
  },

  // Get secondary personalities based on quiz scores (top 2 after primary)
  getSecondaryPersonalities: function () {
    if (!this.personalityScores) return [];

    const scores = Object.entries(this.personalityScores)
      .filter(([key]) => key !== this.personality)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 2)
      .map(([key]) => this.personalities[key]);

    return scores;
  },

  // Smart scoring that uses multiple matching strategies
  scoreProductSmart: function (product, primaryPersonality, secondaryPersonalities) {
    let score = 0;
    const reasons = [];
    const matchedTags = []; // Track which tags actually matched for better reasons
    const tags = (product.tags || []).map((t) => t.toLowerCase());
    const title = (product.title || '').toLowerCase();
    const productType = (product.product_type || '').toLowerCase();

    // === PRIMARY PERSONALITY MATCHING (highest weight) ===

    // 1. Tag matching with partial/substring support (8 points per match, max 40)
    let tagMatches = 0;
    primaryPersonality.tags.forEach((preferredTag) => {
      const tagLower = preferredTag.toLowerCase();
      if (tags.some((t) => t.includes(tagLower) || tagLower.includes(t))) {
        tagMatches++;
        if (matchedTags.length < 3) matchedTags.push(tagLower);
      }
    });
    if (tagMatches > 0) {
      score += Math.min(tagMatches * 8, 40);
      // Use the first actually matched tag for the reason
      const reasonTag = matchedTags[0] || primaryPersonality.tags[0];
      reasons.push(this.getTagReason(reasonTag, primaryPersonality));
    }

    // 2. Title keyword matching (10 points per match, max 30)
    let matchedKeyword = null;
    if (primaryPersonality.titleKeywords) {
      let titleMatches = 0;
      primaryPersonality.titleKeywords.forEach((keyword) => {
        if (title.includes(keyword.toLowerCase())) {
          titleMatches++;
          if (!matchedKeyword) matchedKeyword = keyword;
        }
      });
      if (titleMatches > 0) {
        score += Math.min(titleMatches * 10, 30);
        if (!reasons.length) {
          // Generate specific reason based on matched keyword
          const keywordReason = this.getTagReason(matchedKeyword, primaryPersonality);
          reasons.push(keywordReason);
        }
      }
    }

    // 3. Product type bonus (15 points if matches preferred types)
    if (primaryPersonality.productTypes) {
      const matchesType = primaryPersonality.productTypes.some((type) => {
        return (
          tags.some((t) => t.includes(type)) || productType.includes(type) || title.includes(type)
        );
      });
      if (matchesType) {
        score += 15;
      }
    }

    // === SECONDARY PERSONALITY MATCHING (medium weight) ===
    secondaryPersonalities.forEach((secondaryP) => {
      if (!secondaryP) return;

      // Tag matching for secondary (4 points per match, max 16)
      let secondaryTagMatches = 0;
      secondaryP.tags.forEach((preferredTag) => {
        const tagLower = preferredTag.toLowerCase();
        if (tags.some((t) => t.includes(tagLower))) {
          secondaryTagMatches++;
        }
      });
      if (secondaryTagMatches > 0) {
        score += Math.min(secondaryTagMatches * 4, 16);
      }

      // Title keywords for secondary (5 points per match, max 15)
      if (secondaryP.titleKeywords) {
        let secondaryTitleMatches = 0;
        secondaryP.titleKeywords.forEach((keyword) => {
          if (title.includes(keyword.toLowerCase())) {
            secondaryTitleMatches++;
          }
        });
        if (secondaryTitleMatches > 0) {
          score += Math.min(secondaryTitleMatches * 5, 15);
        }
      }
    });

    // === COLOR PALETTE MATCHING ===
    const paletteAnswer = this.answers.palette;
    if (paletteAnswer && primaryPersonality.colors.includes(paletteAnswer)) {
      const colorKeywords = this.getColorKeywords(paletteAnswer);
      let colorMatches = 0;
      colorKeywords.forEach((keyword) => {
        if (tags.some((t) => t.includes(keyword)) || title.includes(keyword)) {
          colorMatches++;
        }
      });
      if (colorMatches > 0) {
        score += Math.min(colorMatches * 5, 15);
        if (!reasons.some((r) => r.includes('color'))) {
          reasons.push('Matches your color palette');
        }
      }
    }

    // === ACTIVITY-BASED BOOST ===
    const activityAnswer = this.answers.activity;
    if (activityAnswer) {
      const activityTags = this.getActivityTags(activityAnswer);
      let activityMatches = 0;
      activityTags.forEach((tag) => {
        if (tags.some((t) => t.includes(tag)) || title.includes(tag)) {
          activityMatches++;
        }
      });
      if (activityMatches > 0) {
        score += Math.min(activityMatches * 4, 12);
      }
    }

    // === QUALITY BOOSTERS ===
    // Use exact tag matching for quality indicators to avoid false positives
    if (
      tags.some(
        (t) =>
          t === 'best seller' ||
          t === 'bestseller' ||
          t === 'best-seller' ||
          t === 'popular' ||
          t === 'best sellers'
      )
    ) {
      score += 10;
      reasons.push('Customer favorite');
    }
    // Match "new arrival" or tags that are exactly "new" (not words containing "new")
    if (
      tags.some(
        (t) => t === 'new arrival' || t === 'new arrivals' || t === 'new-arrival' || t === 'new'
      )
    ) {
      score += 5;
      if (!reasons.some((r) => r.includes('Fresh'))) {
        reasons.push('Fresh find');
      }
    }
    if (
      tags.some(
        (t) =>
          t === 'restocked' || t === 'restock' || t === 'back in stock' || t === 'back-in-stock'
      )
    ) {
      score += 6;
      reasons.push('Back by popular demand');
    }

    // === UNIVERSAL PRODUCT APPEAL (base score for all products) ===
    // Give every product some base score so we always have results
    score += 5;

    // Cap score at 100
    score = Math.min(score, 100);

    // Ensure at least one reason
    if (reasons.length === 0) {
      reasons.push('Fits your aesthetic');
    }

    return { score, reasons: reasons.slice(0, 3) };
  },

  getTagReason: function (tag, personality) {
    const tagReasons = {
      // Nature/Cottage core
      flora: 'Features natural elements',
      nature: 'Nature-inspired design',
      floral: 'Beautiful floral design',
      flower: 'Lovely flower motifs',
      botanical: 'Botanical artwork',
      plant: 'Plant-themed design',
      garden: 'Garden-inspired',
      mushroom: 'Whimsical mushroom art',
      forest: 'Forest aesthetic',
      leaf: 'Botanical leaf design',

      // Dark Academia/Vintage
      vintage: 'Classic vintage style',
      antique: 'Antique inspired',
      stamp: 'Vintage postal style',
      stamps: 'Vintage postal style',
      book: 'Literary aesthetic',
      letter: 'Epistolary charm',
      library: 'Scholarly appeal',
      ephemera: 'Collectible ephemera',
      postcard: 'Vintage postcard style',
      manuscript: 'Historical charm',

      // Whimsical/Fantasy
      fantasy: 'Fantasy themed',
      magic: 'Magical elements',
      magical: 'Enchanting design',
      fairy: 'Fairytale inspired',
      dream: 'Dreamy aesthetic',
      unicorn: 'Whimsical unicorn art',
      star: 'Celestial star design',
      moon: 'Mystical moon motifs',
      celestial: 'Celestial beauty',
      rainbow: 'Colorful rainbow design',
      butterfly: 'Delicate butterfly art',

      // Bold/Artistic
      colorful: 'Vibrant colors',
      bright: 'Bold bright design',
      art: 'Artistic expression',
      pattern: 'Eye-catching pattern',
      creative: 'Creative design',

      // Product types
      sticker: 'Perfect for decorating',
      stickers: 'Perfect for decorating',
      washi: 'Versatile washi tape',
      journal: 'Ideal for journaling',
      notebook: 'Perfect for writing',

      // Quality indicators
      'new arrivals': 'Fresh new design',
      new: 'Newly added',
      'best seller': 'Customer favorite',
      popular: 'Highly popular',
    };
    return (
      tagReasons[tag.toLowerCase()] || 'Matches your ' + personality.title.toLowerCase() + ' style'
    );
  },

  getColorKeywords: function (palette) {
    const palettes = {
      'earth-tones': [
        'brown',
        'tan',
        'beige',
        'olive',
        'forest',
        'natural',
        'earth',
        'terracotta',
        'rust',
        'umber',
        'sepia',
        'khaki',
        'moss',
        'sage',
        'autumn',
        'fall',
        'warm',
      ],
      'jewel-tones': [
        'emerald',
        'ruby',
        'sapphire',
        'amethyst',
        'deep',
        'rich',
        'dark',
        'burgundy',
        'teal',
        'plum',
        'navy',
        'forest',
        'wine',
        'gold',
        'royal',
        'velvet',
      ],
      pastels: [
        'pink',
        'lavender',
        'mint',
        'peach',
        'soft',
        'light',
        'pastel',
        'blush',
        'coral',
        'baby',
        'powder',
        'pale',
        'rose',
        'lilac',
        'sage',
        'butter',
        'cream',
        'sky',
      ],
      monochrome: [
        'black',
        'white',
        'gray',
        'grey',
        'mono',
        'neutral',
        'minimal',
        'simple',
        'clean',
        'classic',
        'elegant',
        'modern',
      ],
      'warm-neutrals': [
        'cream',
        'ivory',
        'beige',
        'taupe',
        'warm',
        'neutral',
        'linen',
        'sand',
        'oat',
        'vanilla',
        'caramel',
        'honey',
        'natural',
        'nude',
      ],
    };
    return palettes[palette] || [];
  },

  getActivityTags: function (activity) {
    const activityTags = {
      'reading-cafe': [
        'book',
        'books',
        'literary',
        'cozy',
        'coffee',
        'tea',
        'reading',
        'library',
        'novel',
        'story',
        'pages',
        'bookmark',
      ],
      'nature-hike': [
        'nature',
        'botanical',
        'outdoor',
        'adventure',
        'hiking',
        'forest',
        'mountain',
        'trail',
        'plant',
        'flower',
        'leaf',
        'tree',
        'camping',
      ],
      crafting: [
        'craft',
        'diy',
        'creative',
        'handmade',
        'making',
        'scrapbook',
        'collage',
        'art',
        'project',
        'supplies',
        'decorating',
      ],
      'antique-shopping': [
        'vintage',
        'antique',
        'retro',
        'ephemera',
        'old',
        'classic',
        'thrift',
        'collectible',
        'treasure',
        'historical',
        'estate',
      ],
      museum: [
        'art',
        'classical',
        'museum',
        'historical',
        'gallery',
        'exhibit',
        'culture',
        'painting',
        'sculpture',
        'masterpiece',
        'artistic',
      ],
      journaling: [
        'journal',
        'writing',
        'notebook',
        'planner',
        'diary',
        'bullet',
        'bujo',
        'notes',
        'pen',
        'ink',
        'calligraphy',
        'lettering',
      ],
    };
    return activityTags[activity] || [];
  },

  ensureDiversity: function (products, targetCount) {
    if (products.length <= targetCount) return products;

    const selected = [];
    const usedTypes = new Set();

    // Expanded product type detection including title matching
    const getType = (product) => {
      const tags = (product.tags || []).map((t) => t.toLowerCase());
      const title = (product.title || '').toLowerCase();
      const combined = tags.join(' ') + ' ' + title;

      if (combined.includes('sticker')) return 'sticker';
      if (combined.includes('stamp')) return 'stamp';
      if (
        combined.includes('journal') ||
        combined.includes('notebook') ||
        combined.includes('diary')
      ) {
        return 'journal';
      }
      if (combined.includes('washi') || combined.includes('tape')) return 'tape';
      if (combined.includes('paper') || combined.includes('memo') || combined.includes('notepad')) {
        return 'paper';
      }
      if (combined.includes('card') || combined.includes('postcard')) return 'card';
      if (combined.includes('envelope') || combined.includes('letter')) return 'envelope';
      if (combined.includes('pen') || combined.includes('pencil') || combined.includes('marker')) {
        return 'writing';
      }
      if (combined.includes('clip') || combined.includes('pin') || combined.includes('bookmark')) {
        return 'accessory';
      }
      return 'other';
    };

    // First pass: prioritize high-scoring products while ensuring type diversity
    // Take at least one of each type from top scorers
    for (const product of products) {
      if (selected.length >= targetCount) break;
      const type = getType(product);
      if (!usedTypes.has(type)) {
        selected.push(product);
        usedTypes.add(type);
      }
    }

    // Second pass: fill remaining slots with highest-scoring products not yet selected
    for (const product of products) {
      if (selected.length >= targetCount) break;
      if (!selected.includes(product)) {
        selected.push(product);
      }
    }

    // Re-sort selected by score to show best matches first
    selected.sort((a, b) => b.matchScore - a.matchScore);

    return selected;
  },

  displayProducts: function (products) {
    const grid = document.getElementById('styleQuizResultsGrid');

    if (!grid) {
      _err('Results grid not found');
      return;
    }

    if (!products || products.length === 0) {
      this.displayFallback();
      return;
    }

    grid.innerHTML = '';

    products.forEach((product) => {
      // Skip products without a valid handle
      if (!product.handle) {
        if (_isDev) console.warn('Product missing handle:', product.title);
        return;
      }

      const rawPrice =
        product.variants && product.variants[0] ? parseFloat(product.variants[0].price) : 0;
      const price = isNaN(rawPrice) ? 0 : rawPrice;
      // Image with fallback chain
      let image;
      if (product.images && product.images.length > 0 && product.images[0].src) {
        image = product.images[0].src;
      } else if (product.featured_image) {
        image = product.featured_image;
      } else {
        // Placeholder for products without images
        image =
          'data:image/svg+xml,' +
          encodeURIComponent(
            '<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 400 400"><rect fill="#f9e8e6" width="400" height="400"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="#c29392" font-family="system-ui" font-size="48">No Image</text></svg>'
          );
      }

      // Calculate display score with better variance
      // Raw scores typically range from 5 (base) to 100 (max)
      // Map to display range: 70-98% for good UX while showing real differences
      const rawScore = product.matchScore || 5;
      let displayScore;
      if (rawScore >= 80) {
        displayScore = 95 + Math.floor(((rawScore - 80) / 20) * 3); // 95-98%
      } else if (rawScore >= 50) {
        displayScore = 85 + Math.floor(((rawScore - 50) / 30) * 10); // 85-95%
      } else if (rawScore >= 25) {
        displayScore = 78 + Math.floor(((rawScore - 25) / 25) * 7); // 78-85%
      } else {
        displayScore = 70 + Math.floor((rawScore / 25) * 8); // 70-78%
      }
      displayScore = Math.min(98, Math.max(70, displayScore));

      // Build product card
      const card = document.createElement('div');
      card.className = 'style-quiz__product-card';

      // Match score badge
      const scoreBadge = document.createElement('div');
      scoreBadge.className = 'style-quiz__match-score';
      const scoreValue = document.createElement('span');
      scoreValue.className = 'style-quiz__score-value';
      scoreValue.textContent = displayScore + '%';
      const scoreLabel = document.createElement('span');
      scoreLabel.className = 'style-quiz__score-label';
      scoreLabel.textContent = 'match';
      scoreBadge.appendChild(scoreValue);
      scoreBadge.appendChild(scoreLabel);

      // Product link
      const link = document.createElement('a');
      link.href = `/products/${product.handle}?utm_source=style-quiz&utm_medium=quiz&utm_campaign=personality-${this.personality}`;

      // Product image
      const img = document.createElement('img');
      img.src = image;
      img.alt = product.title;
      img.loading = 'lazy';
      img.className = 'style-quiz__product-image';

      // Product info
      const info = document.createElement('div');
      info.className = 'style-quiz__product-info';

      const title = document.createElement('h4');
      title.className = 'style-quiz__product-title';
      title.textContent = product.title;

      const priceEl = document.createElement('p');
      priceEl.className = 'style-quiz__product-price';
      priceEl.textContent = '$' + price.toFixed(2);

      // Why this product section
      const reasonsEl = document.createElement('div');
      reasonsEl.className = 'style-quiz__match-reasons';

      if (product.matchReasons && product.matchReasons.length > 0) {
        product.matchReasons.forEach((reason) => {
          const reasonSpan = document.createElement('span');
          reasonSpan.className = 'style-quiz__reason';
          reasonSpan.textContent = reason;
          reasonsEl.appendChild(reasonSpan);
        });
      }

      info.appendChild(title);
      info.appendChild(priceEl);
      info.appendChild(reasonsEl);

      link.appendChild(img);
      link.appendChild(info);
      card.appendChild(scoreBadge);
      card.appendChild(link);
      grid.appendChild(card);
    });
  },

  displayFallback: function () {
    // Instead of showing empty state, fetch popular/new products
    const grid = document.getElementById('styleQuizResultsGrid');
    if (!grid) return;
    grid.innerHTML = '<div class="style-quiz__loading">Finding products for you...</div>';

    // Try to load best sellers or new arrivals as fallback
    fetch('/collections/all/products.json?limit=50')
      .then((response) => response.json())
      .then((data) => {
        const products = (data.products || []).filter((p) => {
          return p.variants && p.variants[0] && p.variants[0].available;
        });

        // Sort by some criteria - prefer products with images and tags
        products.sort((a, b) => {
          const aScore =
            (a.images && a.images.length ? 10 : 0) + (a.tags && a.tags.length ? a.tags.length : 0);
          const bScore =
            (b.images && b.images.length ? 10 : 0) + (b.tags && b.tags.length ? b.tags.length : 0);
          return bScore - aScore;
        });

        // Take top 8 products
        const fallbackProducts = products.slice(0, 8).map((p) => ({
          ...p,
          matchScore: 70, // Default score for fallback
          matchReasons: ['Curated for you'],
        }));

        if (fallbackProducts.length > 0) {
          this.displayProducts(fallbackProducts);
        } else {
          // Only show browse link if truly no products available
          grid.innerHTML = `
            <div class="style-quiz__no-results">
              <p>Explore our full collection to find pieces that speak to you.</p>
              <a href="/collections/all" class="style-quiz__btn style-quiz__btn--primary">Browse Collection</a>
            </div>
          `;
        }
      })
      .catch((e) => {
        if (_isDev) console.error('[StyleQuiz] fetchProducts failed:', e);
        if (grid) {
          grid.innerHTML = `
          <div class="style-quiz__no-results">
            <p>Explore our full collection to find pieces that speak to you.</p>
            <a href="/collections/all" class="style-quiz__btn style-quiz__btn--primary">Browse Collection</a>
          </div>
        `;
        }
      });
  },

  // Share functionality
  shareOnInstagram: function () {
    if (!this.personality || !this.personalities[this.personality]) {
      _err('No personality set for sharing');
      return;
    }
    const p = this.personalities[this.personality];
    const funFacts = {
      'cottage-curator': 'I see beauty in pressed flowers and handwritten letters',
      'dark-scholar': 'Give me old libraries and the smell of aged paper',
      'minimalist-maker': 'Less is more - every piece in my collection earns its place',
      'whimsical-dreamer': 'Magic lives in the everyday for me',
      'vintage-collector': 'I treasure the stories behind every antique find',
      'bold-expressionist': 'My stationery is as vibrant as my imagination',
    };
    const funFact = funFacts[this.personality] || p.traits[0];
    const text = `${p.emoji} Just took the Stationery Personality Quiz and I'm a ${p.title}!\n\n"${funFact}"\n\nCurious about yours? Take the quiz:\n${window.location.href}\n\n#stationerypersonality #journaling #stationerylover #coracrea`;

    // Try Web Share API first (works on mobile, includes Instagram)
    if (navigator.share) {
      navigator
        .share({
          title: 'My Stationery Personality',
          text: text,
          url: window.location.href,
        })
        .catch(function (err) {
          // User cancelled or error - fall back to copying
          if (err.name !== 'AbortError') {
            StyleQuiz.copyTextForInstagram(text);
          }
        });
    } else {
      // Desktop fallback: copy text and prompt user
      this.copyTextForInstagram(text);
    }
  },

  copyTextForInstagram: function (text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(function () {
          // eslint-disable-next-line no-alert
          alert('Copied to clipboard! Open Instagram and paste in your story or post.');
        })
        .catch(function () {
          StyleQuiz.showCopyFallback(text);
        });
    } else {
      this.showCopyFallback(text);
    }
  },

  showCopyFallback: function (text) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      // eslint-disable-next-line no-alert
      alert('Copied to clipboard! Open Instagram and paste in your story or post.');
    } catch (_err) {
      // eslint-disable-next-line no-alert
      alert('Copy this text to share on Instagram:\n\n' + text);
    }
    document.body.removeChild(textArea);
  },

  copyLink: function (e) {
    const url = window.location.href;
    const btn =
      e && e.currentTarget
        ? e.currentTarget
        : document.querySelector('.style-quiz__share-btn[aria-label="Copy link"]');

    const showSuccess = () => {
      if (btn) {
        const originalHTML = btn.innerHTML;
        btn.innerHTML = '✓';
        btn.setAttribute('aria-label', 'Link copied!');
        setTimeout(() => {
          btn.innerHTML = originalHTML;
          btn.setAttribute('aria-label', 'Copy link');
        }, 2000);
      }
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(url)
        .then(showSuccess)
        .catch((e) => {
          if (_isDev) console.error('[StyleQuiz] clipboard.writeText failed:', e);
          // Fallback for clipboard API failure
          this.copyLinkFallback(url, showSuccess);
        });
    } else {
      // Fallback for older browsers
      this.copyLinkFallback(url, showSuccess);
    }
  },

  copyLinkFallback: function (url, callback) {
    const input = document.createElement('input');
    input.value = url;
    input.style.position = 'fixed';
    input.style.left = '-9999px';
    document.body.appendChild(input);
    input.select();
    input.setSelectionRange(0, url.length);
    try {
      document.execCommand('copy');
      if (callback) callback();
    } catch (err) {
      _err('Copy failed:', err);
    }
    document.body.removeChild(input);
  },

  retakeQuiz: function () {
    // Clear previous results but keep product cache
    this.recommendedProducts = [];
    this.personalityScores = null;
    this.startQuiz();
  },

  viewCollection: function () {
    window.location.href = '/collections/all';
  },
};

// Force square aspect ratio on image wrappers (Safari mobile fix)
function forceSquareImages() {
  const wrappers = document.querySelectorAll('.style-quiz__img-wrap');
  for (let i = 0; i < wrappers.length; i++) {
    const wrap = wrappers[i];
    const width = wrap.offsetWidth;
    if (width > 0) {
      wrap.style.height = width + 'px';
      wrap.style.paddingBottom = '0';
    }
  }
}

// Run multiple times to catch layout shifts
function initSquareImages() {
  forceSquareImages();
  setTimeout(forceSquareImages, 100);
  setTimeout(forceSquareImages, 500);
  setTimeout(forceSquareImages, 1000);
}

// Fix share button SVG colors (override any global CSS)
function fixShareButtonSVGs() {
  const instagramBtn = document.querySelector(
    '.style-quiz__share-btn[aria-label="Share on Instagram"]'
  );
  const copyBtn = document.querySelector('.style-quiz__share-btn[aria-label="Copy link"]');

  if (instagramBtn) {
    const svg = instagramBtn.querySelector('svg');
    const path = instagramBtn.querySelector('path');
    if (svg) {
      svg.style.setProperty('fill', '#5d4037', 'important');
    }
    if (path) {
      path.style.setProperty('fill', '#5d4037', 'important');
    }
  }

  if (copyBtn) {
    const copySvg = copyBtn.querySelector('svg');
    const paths = copyBtn.querySelectorAll('path');
    if (copySvg) {
      copySvg.style.setProperty('stroke', '#5d4037', 'important');
    }
    paths.forEach(function (path) {
      path.style.setProperty('fill', 'none', 'important');
      path.style.setProperty('stroke', '#5d4037', 'important');
      path.style.setProperty('stroke-width', '2', 'important');
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function () {
    StyleQuiz.init();
    initSquareImages();
    window.addEventListener('resize', forceSquareImages);
    fixShareButtonSVGs();
    // Also fix after a delay in case of late CSS loading
    setTimeout(fixShareButtonSVGs, 500);
    setTimeout(fixShareButtonSVGs, 1500);
  });
} else {
  StyleQuiz.init();
  initSquareImages();
  window.addEventListener('resize', forceSquareImages);
  fixShareButtonSVGs();
  setTimeout(fixShareButtonSVGs, 500);
  setTimeout(fixShareButtonSVGs, 1500);
}
